import { useRenameBeneficiaryMutation } from "@/modules/beneficiary/services";
import { renameBeneficiaryValidationSchema } from "@/modules/beneficiary/validations";
import { NotificationScope } from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import { Notification } from "@/shared/components";
import { useNotify } from "@/shared/hooks";
import { FormContainer } from "@ab.uitools/ui-library/components/FormContainer";
import { Modal } from "@ab.uitools/ui-library/components/Modal";
import type { TFormData } from "@ab.uitools/ui-library/types/globalTypes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { RenameBeneficiaryForm } from "./RenameBeneficiaryForm";

interface Props {
  id: string;
  isOpen: boolean;
  beneficiaryName: string;
  onClose: () => void;
  refetch: () => void;
}

export const RenameBeneficiaryModal = ({
  id,
  isOpen,
  beneficiaryName,
  onClose,
  refetch,
}: Props) => {
  const { t } = useTranslation();
  const { showErrorAlertNotification, showSuccessSnackbar } = useNotify();
  const [currentName, setCurrentName] = useState<string>(beneficiaryName);

  const [renameBeneficiary, { isLoading }] = useRenameBeneficiaryMutation();

  const handleSubmit = (data: TFormData, isDirty: boolean) => {
    if (!isDirty) return;
    renameBeneficiary({ id, name: data.name })
      .unwrap()
      .then(() => {
        refetch();
        onClose();
        showSuccessSnackbar(t("alerts.beneficiaryHasBeenSuccessfullyRenamed"));
      })
      .catch((err: IError) => {
        showErrorAlertNotification(err, NotificationScope.Modal);
      });
  };

  return (
    <Modal
      closeIcon
      closeOnOutsideClick={false}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={() => {}}
      titleProps={{
        title: t("beneficiary.renameModal.title"),
        size: "large",
      }}
      buttonProps={{
        confirm: {
          formId: "rename-beneficiary-form",
          buttonText: t("general.save"),
          buttonActionType: "submit",
          isLoading,
          disabled: isLoading || currentName === beneficiaryName,
        },
        cancel: { buttonText: t("general.cancel") },
      }}
    >
      <Notification className="mb-8" scope={NotificationScope.Modal}/>
      <FormContainer
        mode="onBlur"
        reValidateMode="onBlur"
        className="full-width"
        formId="rename-beneficiary-form"
        initialValues={{ name: beneficiaryName }}
        validationScheme={renameBeneficiaryValidationSchema({ t })}
        onSubmit={(data, formState) => handleSubmit(data, formState.isDirty)}
      >
        <RenameBeneficiaryForm
          defaultValue={beneficiaryName}
          onNameChange={setCurrentName}
        />
      </FormContainer>
    </Modal>
  );
};
